import { Copy, MailCheck, MailPlus, MoreHorizontal, RotateCw, Search, Send, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Avatar, CopyButton, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Toolbar } from "@/components/layout/page";
import { CallPager } from "@/features/call-report";
import { ApiError } from "@/lib/api";
import {
  EMAIL_LABELS, INVITATIONS_PAGE_SIZE, invitationPage, batchRole, bulkOutcomes, bulkSummary, emailOutcome, invitationStatus, resendLabel, withArticle,
} from "@/lib/invitations";
import { useDeleteInvitation, useDeleteInvitations, useInvitations, useResendInvitation } from "@/lib/queries";
import {
  ROLE_LABELS, type Invitation, type InvitationBulkResult, type InvitationBulkSent,
  type InvitationSent, type InvitationStatus, type Role,
} from "@/lib/types";
import { formatDateTime, relativeTime } from "@/lib/utils";

/**
 * The Users page's Invitations tab (ADR-028 in `platform`): who has been asked to join, and where
 * each invitation stands.
 *
 * Rows stay once expired or accepted, so an admin can see what was sent. An expired one is sent
 * again ("Re-invite") or deleted. Sending again makes a new link and ends the old one, which is also
 * how an admin gets a link they did not copy: the server returns it only in the answer to a send.
 */
export function InvitationsPanel({
  onInvite, onSent,
}: {
  onInvite: () => void;
  /** An invitation sent again, with its new link, for the page to show. */
  onSent: (sent: InvitationSent) => void;
}) {
  const toast = useToast();
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(INVITATIONS_PAGE_SIZE);
  const [status, setStatus] = React.useState<"all" | InvitationStatus>("all");
  // Status is decided by the server when it answers, so a list left open refetches to move a row
  // from pending to expired without a reload.
  const { data, isPending, error, refetch } = useInvitations({ refetchInterval: 60_000 });
  const resend = useResendInvitation();
  const remove = useDeleteInvitation();
  const removeMany = useDeleteInvitations();
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = React.useState<number[] | null>(null);
  const [deleting, setDeleting] = React.useState<Invitation | undefined>();

  const { rows, total, page: currentPage } = invitationPage(data ?? [], search, status, page, pageSize);
  const filtered = search.trim() !== "" || status !== "all";
  // Selections apply to this page, so changing filters cannot hide selected invitations.
  const selectedIds = rows.filter((row) => selected.has(row.id)).map((row) => row.id);
  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const selectPage = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (selectPage.current) selectPage.current.indeterminate = selectedIds.length > 0 && !allSelected;
  }, [selectedIds.length, allSelected]);
  React.useEffect(() => { setSelected(new Set()); }, [search, status, pageSize, currentPage]);
  const toggleSelected = (id: number, checked: boolean) => setSelected((current) => {
    const next = new Set(current);
    if (checked) next.add(id);
    else next.delete(id);
    return next;
  });
  React.useEffect(() => {
    if (data && page !== currentPage) setPage(currentPage);
  }, [data, page, currentPage]);
  const clearFilters = () => { setSearch(""); setStatus("all"); setPage(0); };
  const count = (s: InvitationStatus) => (data ?? []).filter((i) => i.status === s).length;

  const sendAgain = async (invitation: Invitation) => {
    try {
      onSent(await resend.mutateAsync(invitation.id));
    } catch (e) {
      toast.error("Could not send it again", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Card className="overflow-hidden">
      <Toolbar className="flex-nowrap overflow-x-auto [&>*]:shrink-0">
        <div className="w-56">
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search name or email"
            leading={<Search />}
            aria-label="Search invitations by name or email"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v as typeof status); setPage(0); }}>
          <SelectTrigger className="w-44" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All invitations</SelectItem>
            <SelectItem value="pending">Pending ({count("pending")})</SelectItem>
            <SelectItem value="expired">Expired ({count("expired")})</SelectItem>
            <SelectItem value="accepted">Accepted ({count("accepted")})</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
          <SelectTrigger className="w-32" aria-label="Invitations per page"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[25, 50, 100].map((size) => <SelectItem key={size} value={String(size)}>{size} per page</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
        <span className="ml-auto whitespace-nowrap text-xs text-text-muted" aria-live="polite">
          {data ? total > 0
            ? `${currentPage * pageSize + 1}–${currentPage * pageSize + rows.length} of ${total} invitations`
            : "0 invitations" : null}
        </span>
      </Toolbar>

      {selectedIds.length > 0 && (
        <Toolbar>
          <span className="text-sm text-text-muted">{selectedIds.length} selected on this page</span>
          <Button variant="danger" size="sm" onClick={() => setBulkDeleting([...selectedIds])}>
            <Trash2 /> Delete selected
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear selection</Button>
        </Toolbar>
      )}

      {isPending ? (
        <TableSkeleton cols={7} />
      ) : error ? (
        <ErrorState
          error={error}
          title="Could not load invitations"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={MailPlus}
          title={filtered ? "No invitations match those filters" : "No invitations yet"}
          description={
            filtered
              ? "Try another name or email, or clear the filters."
              : "Invite somebody by email. They choose their own password, and their campaigns and skills are ready when they accept."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
            ) : (
              <Button variant="default" onClick={onInvite}>
                <MailPlus />
                Invite user
              </Button>
            )
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input ref={selectPage} type="checkbox" className="size-3.5 accent-accent-9"
                  aria-label="Select all invitations on this page" checked={allSelected}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((row) => row.id)) : new Set())} />
              </TableHead>
              <TableHead>Person</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joins with</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((invitation) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                selected={selected.has(invitation.id)}
                onSelect={(checked) => toggleSelected(invitation.id, checked)}
                busy={resend.isPending && resend.variables === invitation.id}
                onResend={() => void sendAgain(invitation)}
                onDelete={() => setDeleting(invitation)}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {!isPending && !error && total > 0 && (
        <CallPager page={currentPage} count={rows.length} total={total}
          onChange={setPage} busy={false} pageSize={pageSize} />
      )}

      <ConfirmDialog
        open={bulkDeleting !== null}
        onOpenChange={(open) => { if (!open && !removeMany.isPending) setBulkDeleting(null); }}
        title={`Delete ${bulkDeleting?.length ?? 0} invitations?`}
        description="Selected invitation records will be permanently removed. Pending and expired links will stop working. Accounts created from accepted invitations will remain unchanged."
        confirmLabel="Delete invitations"
        destructive
        loading={removeMany.isPending}
        onConfirm={async () => {
          if (!bulkDeleting?.length) return;
          try {
            await removeMany.mutateAsync(bulkDeleting);
            toast.success(`${bulkDeleting.length} invitations deleted`);
            setSelected(new Set());
            setBulkDeleting(null);
          } catch (e) {
            toast.error("Could not delete invitations", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete this invitation?"
        description={
          deleting?.status === "accepted" ? (
            <>
              The record of inviting <span className="font-medium">{deleting.email}</span> goes. Their
              account stays as it is.
            </>
          ) : (
            <>
              The link sent to <span className="font-medium">{deleting?.email}</span> stops working.
              You can invite them again afterwards.
            </>
          )
        }
        confirmLabel="Delete invitation"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Invitation deleted", deleting.email);
            setDeleting(undefined);
          } catch (e) {
            toast.error("Could not delete", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Card>
  );
}

function InvitationRow({
  invitation, busy, selected, onSelect, onResend, onDelete,
}: {
  invitation: Invitation;
  busy: boolean;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onResend: () => void;
  onDelete: () => void;
}) {
  const state = invitationStatus(invitation);
  const outcome = emailOutcome(invitation);
  const again = resendLabel(invitation);
  const campaigns = invitation.campaigns.map((c) => c.name ?? "Deleted campaign");
  const skills = invitation.skills.map((s) => `${s.slug} ${s.level}`);

  return (
    <TableRow className="[&>td]:h-11">
      <TableCell>
        <input type="checkbox" className="size-3.5 accent-accent-9"
          aria-label={`Select invitation for ${invitation.email}`} checked={selected}
          onChange={(e) => onSelect(e.target.checked)} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2.5">
          <Avatar name={invitation.display_name} email={invitation.email} />
          <div className="min-w-0">
            <span className="block truncate font-medium text-text">
              {invitation.display_name || invitation.email}
            </span>
            {invitation.display_name && (
              <span className="block truncate text-xs text-text-muted">{invitation.email}</span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge tone={invitation.role === "superadmin" ? "accent" : "neutral"}>
          {ROLE_LABELS[invitation.role]}
        </Badge>
      </TableCell>
      <TableCell className="max-w-44 text-xs text-text-muted">
        {campaigns.length === 0 && skills.length === 0 ? (
          "—"
        ) : (
          <>
            {campaigns.length > 0 && (
              <span className="block truncate" title={campaigns.join(", ")}>
                {campaigns.join(", ")}
              </span>
            )}
            {skills.length > 0 && (
              <span className="block truncate font-mono" title={skills.join(", ")}>
                {skills.join(", ")}
              </span>
            )}
          </>
        )}
      </TableCell>
      <TableCell>
        <span className="flex flex-col items-start gap-0.5">
          <Badge tone={state.tone} size="sm">
            {state.label}
          </Badge>
          <span className="whitespace-nowrap text-xs text-text-muted">{state.detail}</span>
        </span>
      </TableCell>
      <TableCell className="text-xs text-text-muted">
        <span className="block whitespace-nowrap">{relativeTime(invitation.last_sent_at)}</span>
        <span className="block">
          {outcome === "failed" ? (
            <Tooltip content={invitation.email_error ?? ""}>
              <span className="text-warning-11">{EMAIL_LABELS.failed}</span>
            </Tooltip>
          ) : (
            EMAIL_LABELS[outcome]
          )}
          {invitation.sent_count > 1 && ` · sent ${invitation.sent_count} times`}
        </span>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              loading={busy}
              aria-label={`Actions for the invitation to ${invitation.email}`}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {again && (
              <>
                <DropdownMenuItem onSelect={onResend}>
                  {invitation.status === "expired" ? <RotateCw /> : <Send />}
                  {again}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem destructive onSelect={onDelete}>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/**
 * The answer to sending an invitation: whether the email went, and the link. The link is shown
 * here and nowhere else, since core-api keeps only a digest of it.
 */
export function InvitationSentDialog({
  sent, open, onOpenChange,
}: {
  /** Kept while the dialog animates shut, so it does not empty as it closes. */
  sent: InvitationSent | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const outcome = sent ? emailOutcome(sent) : "link";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        {sent && (
          <>
            <DialogHeader>
              <DialogTitle>{outcome === "emailed" ? "Invitation sent" : "Invitation ready"}</DialogTitle>
              <DialogDescription>
                {sent.display_name ? `${sent.display_name} (${sent.email})` : sent.email} joins as{" "}
                {withArticle(sent.role)} when they accept.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {outcome === "emailed" ? (
                <p className="flex items-center gap-2 text-xs text-success-11">
                  <MailCheck className="size-3.5 shrink-0" aria-hidden />
                  Emailed to {sent.email}. You can also send them the link yourself.
                </p>
              ) : outcome === "failed" ? (
                <Callout tone="warning">
                  The email could not be sent ({sent.email_error}). Copy the link and send it to them
                  yourself, or send the invitation again later.
                </Callout>
              ) : (
                <Callout tone="info">
                  This server does not send email yet, so copy the link and send it to them yourself.
                </Callout>
              )}
              <Field
                label="Invitation link"
                htmlFor="invitation-link"
                hint={`It works once, until ${formatDateTime(sent.expires_at)}. It is not shown again: to get a new one, send the invitation again.`}
              >
                <Input
                  readOnly
                  value={sent.invite_url}
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                  trailing={<CopyButton value={sent.invite_url} label="Copy the link" />}
                />
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button variant="default" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The answer to inviting several people: for each, whether the email went and their link, or why
 * they were not invited. Like the single answer, it is the only place these links are shown.
 */
export function InvitationsSentDialog({
  sent, open, onOpenChange,
}: {
  /** Kept while the dialog animates shut, so it does not empty as it closes. */
  sent: InvitationBulkSent | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const results = sent?.results ?? [];
  const counts = bulkOutcomes(results);
  const made = results.flatMap((r) => (r.invitation ? [r.invitation] : []));
  const shared = batchRole(results);
  const title =
    counts.invited === 0
      ? "Nobody was invited"
      : `${counts.invited} invitation${counts.invited === 1 ? "" : "s"} ${counts.emailed === counts.invited ? "sent" : "ready"}`;

  const copyAll = async () => {
    const lines = made.map((i) => `${i.email}: ${i.invite_url}`).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Links copied", `${made.length} link${made.length === 1 ? "" : "s"}, one per line`);
    } catch {
      toast.error("Could not copy", "Copy each link with the button beside it.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        {sent && (
          <>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                {bulkSummary(results)}.
                {/* Rows can carry their own role, so say it only when it is everybody's. */}
                {made.length > 0 && (
                  shared
                    ? ` Each joins as ${withArticle(shared)} when they accept.`
                    : " Each joins on the role beside their name when they accept."
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {!sent.email_enabled && counts.invited > 0 && (
                <Callout tone="info">
                  This server does not send email yet, so copy each link and send it to them yourself.
                </Callout>
              )}
              {counts.failed > 0 && (
                <Callout tone="warning">
                  {counts.failed === 1 ? "One email" : `${counts.failed} emails`} could not be sent. Copy
                  those links and send them yourself, or send the invitations again later.
                </Callout>
              )}
              <ul
                aria-label="Invitations"
                className="max-h-96 divide-y divide-border overflow-y-auto rounded-md border border-border"
              >
                {results.map((result) => (
                  <BulkResultRow key={result.email} result={result} showRole={shared === null} />
                ))}
              </ul>
              {made[0] && (
                <p className="text-xs text-text-muted">
                  Each link works once, until {formatDateTime(made[0].expires_at)}. They are not shown
                  again: to get a new one, send that invitation again from the list.
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              {made.length > 1 && (
                <Button variant="outline" onClick={() => void copyAll()}>
                  <Copy />
                  Copy all links
                </Button>
              )}
              <Button variant="default" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BulkResultRow({
  result, showRole,
}: {
  result: InvitationBulkResult;
  /** Only where the batch did not all join on one role, which the dialog then says instead. */
  showRole: boolean;
}) {
  const sent = result.invitation;
  if (!sent) {
    return (
      <li className="flex items-start gap-3 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-text">{result.email}</span>
          <span className="block text-xs text-text-muted">{result.error}</span>
        </div>
        <Badge tone="danger" size="sm">
          Not invited
        </Badge>
      </li>
    );
  }
  const outcome = emailOutcome(sent);
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-text">{sent.display_name || sent.email}</span>
        {sent.display_name && <span className="block truncate text-xs text-text-muted">{sent.email}</span>}
        {outcome === "failed" && (
          <span className="block truncate text-xs text-warning-11" title={sent.email_error ?? undefined}>
            {sent.email_error}
          </span>
        )}
      </div>
      {showRole && (
        <Badge tone="outline" size="sm">
          {ROLE_LABELS[sent.role as Role]}
        </Badge>
      )}
      <Badge tone={outcome === "emailed" ? "success" : outcome === "failed" ? "warning" : "neutral"} size="sm">
        {EMAIL_LABELS[outcome]}
      </Badge>
      <CopyButton value={sent.invite_url} label={`Copy the link for ${sent.email}`} />
    </li>
  );
}
